
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { ChartLine } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import RepricingGapChart from './charts/RepricingGapChart';
import DurationGapChart from './charts/DurationGapChart';
import MVESimChart from './charts/MVESimChart';
import NIISimChart from './charts/NIISimChart';
import { defaultRepricingGapData, defaultDurationGapData } from './data/forecastingData';

const GapAnalysis = () => {
  const [rateScenario, setRateScenario] = useState("parallel");
  const [shockMagnitude, setShockMagnitude] = useState("200");
  const { toast } = useToast();

  const handleRunSimulation = () => {
    toast({
      title: "Simulation Completed",
      description: `Gap analysis simulation with ${rateScenario} rate shock of ${shockMagnitude} bps completed`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <Card className="md:col-span-4">
          <CardHeader>
            <CardTitle className="flex items-center">
              <ChartLine className="h-5 w-5 mr-2" />
              Simulation Parameters
            </CardTitle>
            <CardDescription>
              Configure interest rate shock scenarios for gap analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rate-scenario">Interest Rate Shock Scenario</Label>
              <Select value={rateScenario} onValueChange={setRateScenario}>
                <SelectTrigger id="rate-scenario">
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="parallel">Parallel Shift</SelectItem>
                  <SelectItem value="steepening">Yield Curve Steepening</SelectItem>
                  <SelectItem value="flattening">Yield Curve Flattening</SelectItem>
                  <SelectItem value="short-rate">Short Rate Shock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="shock-magnitude">Shock Magnitude (bps)</Label>
              <Select value={shockMagnitude} onValueChange={setShockMagnitude}>
                <SelectTrigger id="shock-magnitude">
                  <SelectValue placeholder="Select magnitude" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="100">100 bps</SelectItem>
                  <SelectItem value="200">200 bps</SelectItem>
                  <SelectItem value="300">300 bps</SelectItem>
                  <SelectItem value="400">400 bps</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="pt-4">
              <Button className="w-full" onClick={handleRunSimulation}>
                Run Simulation
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <Card className="md:col-span-8">
          <CardHeader>
            <CardTitle>Gap Analysis Results</CardTitle>
            <CardDescription>
              Comprehensive gap analysis across multiple metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs defaultValue="repricing" className="w-full">
              <div className="border-b px-6">
                <TabsList className="h-12">
                  <TabsTrigger value="repricing">Repricing Gap</TabsTrigger>
                  <TabsTrigger value="duration">Duration Gap</TabsTrigger>
                  <TabsTrigger value="mve">MVE Simulation</TabsTrigger>
                  <TabsTrigger value="nii">NII Simulation</TabsTrigger>
                </TabsList>
              </div>
              <div className="p-6">
                <TabsContent value="repricing" className="mt-0 space-y-6">
                  <div className="h-[300px]">
                    <RepricingGapChart />
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Repricing Gap Analysis</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time Bucket</TableHead>
                          <TableHead className="text-right">Rate Sensitive Assets (₹ Cr)</TableHead>
                          <TableHead className="text-right">Rate Sensitive Liabilities (₹ Cr)</TableHead>
                          <TableHead className="text-right">Gap (₹ Cr)</TableHead>
                          <TableHead className="text-right">Cumulative Gap (₹ Cr)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {defaultRepricingGapData.map((item) => (
                          <TableRow key={item.bucket}>
                            <TableCell>{item.bucket}</TableCell>
                            <TableCell className="text-right">{item.assets}</TableCell>
                            <TableCell className="text-right">{item.liabilities}</TableCell>
                            <TableCell className={`text-right ${parseFloat(item.gap) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {item.gap}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${parseFloat(item.cumulativeGap) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {item.cumulativeGap}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                
                <TabsContent value="duration" className="mt-0 space-y-6">
                  <div className="h-[300px]">
                    <DurationGapChart />
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">Duration Gap Analysis</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Asset/Liability Category</TableHead>
                          <TableHead className="text-right">Amount (₹ Cr)</TableHead>
                          <TableHead className="text-right">Modified Duration</TableHead>
                          <TableHead className="text-right">Weight</TableHead>
                          <TableHead className="text-right">Weighted Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {defaultDurationGapData.map((item) => (
                          <TableRow key={item.category}>
                            <TableCell>{item.category}</TableCell>
                            <TableCell className="text-right">{item.amount}</TableCell>
                            <TableCell className="text-right">{item.duration}</TableCell>
                            <TableCell className="text-right">{item.weight}</TableCell>
                            <TableCell className="text-right">{item.weightedDuration}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-muted/50">
                          <TableCell className="font-medium">Duration Gap</TableCell>
                          <TableCell colSpan={3}></TableCell>
                          <TableCell className="text-right font-medium">1.25</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                
                <TabsContent value="mve" className="mt-0 space-y-6">
                  <div className="h-[300px]">
                    <MVESimChart />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Market Value of Equity Impact</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Base MVE</span>
                          <span className="font-medium">₹ 8,250.00 Cr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>MVE after shock</span>
                          <span className="font-medium text-red-500">₹ 7,685.50 Cr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Change in MVE</span>
                          <span className="font-medium text-red-500">₹ -564.50 Cr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>% Change</span>
                          <span className="font-medium text-red-500">-6.84%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Risk Assessment</h4>
                      <p className="text-sm mb-2">
                        With a duration gap of 1.25, a {rateScenario} interest rate increase of {shockMagnitude} bps results in an estimated MVE decline of 6.84%.
                      </p>
                      <p className="text-sm">
                        <span className="font-medium">Recommendation:</span> Consider increasing fixed rate assets or reducing liability duration to narrow the gap.
                      </p>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="nii" className="mt-0 space-y-6">
                  <div className="h-[300px]">
                    <NIISimChart />
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Net Interest Income Impact</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>Base NII</span>
                          <span className="font-medium">₹ 420.75 Cr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>NII after shock</span>
                          <span className="font-medium text-red-500">₹ 395.50 Cr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Change in NII</span>
                          <span className="font-medium text-red-500">₹ -25.25 Cr</span>
                        </div>
                        <div className="flex justify-between">
                          <span>% Change</span>
                          <span className="font-medium text-red-500">-6.00%</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h4 className="font-medium mb-2">Risk Assessment</h4>
                      <p className="text-sm mb-2">
                        The negative impact on NII indicates liability-sensitive position in the short term. NII at risk is 6.00% under current scenario.
                      </p>
                      <p className="text-sm">
                        <span className="font-medium">Recommendation:</span> Consider interest rate derivatives or rebalancing short-term assets to mitigate impact.
                      </p>
                    </div>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Interpretation & Recommendations</CardTitle>
          <CardDescription>Analysis summary and actionable insights</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Repricing Risk</h4>
              <div className="bg-muted p-3 rounded-md text-sm h-[200px] overflow-y-auto">
                <p className="mb-2">
                  Analysis shows negative repricing gaps in the 3-6 month and 6-12 month buckets, indicating that more liabilities than assets will reprice during these periods.
                </p>
                <p className="mb-2">
                  <span className="font-medium text-amber-600">Risk Exposure:</span> In a rising rate environment, this creates pressure on NII as funding costs increase before asset yields adjust.
                </p>
                <p>
                  <span className="font-medium text-green-600">Recommendation:</span> Increase floating rate assets in these buckets or extend liability duration through term deposits and longer-term borrowings.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Duration Gap Risk</h4>
              <div className="bg-muted p-3 rounded-md text-sm h-[200px] overflow-y-auto">
                <p className="mb-2">
                  The positive duration gap of 1.25 indicates that assets have longer duration than liabilities, creating exposure to interest rate changes.
                </p>
                <p className="mb-2">
                  <span className="font-medium text-amber-600">Risk Exposure:</span> When rates rise, asset values fall more than liability values, reducing economic value of equity.
                </p>
                <p>
                  <span className="font-medium text-green-600">Recommendation:</span> Consider using interest rate swaps to reduce duration gap, or adjust investment portfolio to include shorter duration securities.
                </p>
              </div>
            </div>
            
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Economic Value Risk</h4>
              <div className="bg-muted p-3 rounded-md text-sm h-[200px] overflow-y-auto">
                <p className="mb-2">
                  MVE simulation indicates a 6.84% decline in economic value under a {rateScenario} {shockMagnitude} bps shock scenario.
                </p>
                <p className="mb-2">
                  <span className="font-medium text-amber-600">Risk Exposure:</span> Interest rate movements could significantly affect the market value of the balance sheet.
                </p>
                <p>
                  <span className="font-medium text-green-600">Recommendation:</span> Review asset-liability mix with focus on reducing interest rate sensitivity. Consider implementing hedging strategies to protect MVE from adverse rate movements.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GapAnalysis;
